/**
 * Where a spotlight's message card sits, as a pure function so the arithmetic is
 * testable without a DOM. Rectangles are in viewport coordinates.
 */
export type SpotlightRect = { top: number; left: number; width: number; height: number };
export type SpotlightSize = { width: number; height: number };

/** Space kept between the card and the viewport edge. */
const EDGE = 12;
/** Space kept between the card and the highlighted element. */
const GAP = 12;

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

export function placeSpotlightCard(
  hole: SpotlightRect | null,
  card: SpotlightSize,
  viewport: SpotlightSize,
): { top: number; left: number } {
  const maxLeft = Math.max(EDGE, viewport.width - card.width - EDGE);
  const maxTop = Math.max(EDGE, viewport.height - card.height - EDGE);

  if (!hole) {
    return {
      top: clamp((viewport.height - card.height) / 2, EDGE, maxTop),
      left: clamp((viewport.width - card.width) / 2, EDGE, maxLeft),
    };
  }

  const left = clamp(hole.left + hole.width / 2 - card.width / 2, EDGE, maxLeft);
  const below = hole.top + hole.height + GAP;
  const above = hole.top - GAP - card.height;
  const fitsBelow = below + card.height <= viewport.height - EDGE;
  const fitsAbove = above >= EDGE;

  // Prefer the roomier side so the card never crowds the thing it describes.
  if (fitsBelow && (!fitsAbove || hole.top + hole.height / 2 < viewport.height / 2)) {
    return { top: below, left };
  }
  if (fitsAbove) return { top: above, left };
  // The element fills the screen (a long list on a phone): dock to the bottom,
  // and the member can scroll what is behind it.
  return { top: maxTop, left };
}

/**
 * The four strips around a hole that catch clicks, so only the highlighted
 * element can be pressed. A hole partly off screen just gives narrower strips.
 */
export function spotlightBlockers(hole: SpotlightRect, viewport: SpotlightSize): SpotlightRect[] {
  const top = clamp(hole.top, 0, viewport.height);
  const bottom = clamp(hole.top + hole.height, 0, viewport.height);
  const left = clamp(hole.left, 0, viewport.width);
  const right = clamp(hole.left + hole.width, 0, viewport.width);
  return [
    { top: 0, left: 0, width: viewport.width, height: top },
    { top: bottom, left: 0, width: viewport.width, height: viewport.height - bottom },
    { top, left: 0, width: left, height: bottom - top },
    { top, left: right, width: viewport.width - right, height: bottom - top },
  ];
}

/**
 * For a dialog's `onInteractOutside`: pressing the tutorial's own card or dimmed
 * area must not close the dialog underneath it.
 */
export function keepOpenForTutorial(event: {
  target: EventTarget | null;
  detail?: { originalEvent?: Event };
  preventDefault: () => void;
}) {
  const source = (event.detail?.originalEvent?.target ?? event.target) as Element | null;
  if (source?.closest?.('[data-tutorial-ui]')) event.preventDefault();
}
